<template>
  <div>
    <Head title="Edit Sales Order" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-orange-400 hover:text-orange-600" href="/orders">Sales Orders</Link>
      <span class="text-orange-400 font-medium">/</span> Edit
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow">
      <form @submit.prevent="update">
        <div class="pr-8 pl-8 pt-5 pb-2">
          <multiselect
            v-model="form.customer"
            placeholder="Search Customer"
            :options="customers"
            :close-on-select="true"
            :searchable="true"
            :error="form.errors.customer"
            :style="form.errors.customer ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
            @search-change="onSearchCustomersChange"
            @input="onSelectedCustomer"
          />
          <span
            class="mt-3"
          >
            <span class="form-error">{{ form.errors.customer }}</span>
          </span>
        </div>
        <div class="pr-8 pl-8 pt-5 pb-2">
          <file-input v-model="form.photo" :error="form.errors.photo" type="file" accept="image/*" label="LPO" />
          <span
            class="mt-3"
          >
            <span class="form-error">{{ form.errors.photo }}</span>
          </span>
        </div>
        <div class="grid grid-cols-1 gap-4 pr-8 pl-8 pt-5 pb-2">
          <div
            v-for="(orderItem, index) in form.orders"
            :key="index"
            class="flex flex-wrap"
          >
            <div class="pb-2 pr-6 w-7/12">
              <multiselect
                id="part_number"
                :key="index"
                v-model="orderItem.part_number"
                placeholder="Search SKU"
                :options="skus"
                :close-on-select="true"
                :searchable="true"
                :error="form.errors.part_number"
                :style="form.errors.part_number ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                @search-change="onSearchSkusChange"
                @input="onSelectedSku"
              />
              <span
                v-if="isValidationErrors(`orders.${index}.part_number`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`orders.${index}.part_number`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-3/12">
              <text-input
                :key="index"
                v-model="orderItem.quantity"
                class="pb-2"
                :error="form.errors.quantity"
                placeholder="Quantity"
              />
              <span
                v-if="isValidationErrors(`orders.${index}.quantity`)"
                class="mt-3"
              >
                <span class="form-error">{{ showValidationErrors(`orders.${index}.quantity`) }}</span>
              </span>
            </div>
            <div class="pb-2 pr-6 w-2/12">
              <div class="w-full lg:w-full">
                <svg
                  v-show="form.orders.length > 1"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" style="color: red;" @click="removeOrderItem(index, form.orders)"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="w-full lg:w-full">
                <svg
                  v-show="index === countOrders"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" @click="addOrderItem"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div class="pr-8 pl-8 pt-5 pb-2">
          <select-input v-model="form.voucher_id" :error="form.errors.voucher_id">
            <option :value="null">-Select Voucher Code-</option>
            <option v-for="voucher in vouchers" :key="voucher.id" :value="voucher.id">{{ voucher.code }}</option>
          </select-input>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-orange" type="submit">Update</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>
    
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import Multiselect from '@vueform/multiselect'
import SelectInput from '@/Shared/SelectInput'
import throttle from 'lodash/throttle'
import FileInput from '@/Shared/FileInput'
    
export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    Multiselect,
    FileInput,
    SelectInput,
  },
  layout: Layout,
  remember: 'form',
  props: {
    skus: Object,
    customers: Object,
    orderItems: Object,
    products: Object,
    order: Object,
    vouchers: Object,
    cities: Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        customer: this.order.customer_id,
        voucher_id: this.order.voucher_id,
        orders: this.products,
        photo: null,
        city_id: this.order.city_id,
        physical_address: this.order.physical_address,
      }),
    }
  },
  computed: {
    countOrders() {
      if (this.form.orders.length === 0) {
        return 0
      }
      return this.form.orders.length - 1
    },
  },
  methods: {
    update() {
      this.form.post(`/orders/${this.order.id}`)
    },
    addOrderItem(e) {
      e.preventDefault()
      this.form.orders.push({
        part_number: null,
        quantity: null,
      })
    },
    removeOrderItem(index, orders) {
      orders.splice(index, 1)
    },
    isValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || ''
        return !!error
      }
      return false
    },
    showValidationErrors(value) {
      if (this.form.errors) {
        const error = this.form.errors[value] || ''
        return error || ''
      }
      return ''
    },
    onSearchSkusChange: throttle(function (search) {
      this.$inertia.get(`/orders/${this.order.id}/edit`, {search}, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, 200),
    onSelectedCustomer(customer, id) {
      this.form.customer = customer
    },
    onSearchCustomersChange: throttle(function (search) {
      this.$inertia.get(`/orders/${this.order.id}/edit`, {search}, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    }, 200),
    onSelectedSku(part_number, id) {
      this.form.part_number = part_number
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
    